import * as Styled from './LeftForm.styles';

import { allowsDecimals, allowsOnlyNumbers } from 'utils/numberInput';

import { Form } from 'features/common/components/Form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemo, useEffect, useState } from 'react';
// Standalone-dark ticket-details keeps develop's swatch-grid picker; the rewritten HSV picker
// (forms/pickers/PunchupColorPicker, light tokens) is green-only. Duplication is correct doctrine.
import { PunchupColorPicker } from './PunchupColorPicker';
import { FlexContainer } from 'features/common/components/FlexContainer';
import {
	useGetTaxes,
	calculateBasePrice,
	calculateFinalPrice,
} from 'features/CreateTicket/components/Steps/TicketDetails/utils';
import { useGetLastEvent } from 'features/BoxOffice/hooks/useGetLastEvent';

// TODO: Uncomment when backend is ready
// import { Toggle } from 'features/common/components/Toggle';
// import { ToolTip } from 'features/common/components/InfoTooltip';
// import { Icon } from 'features/common/components/Icon';

const LeftForm: React.FC = () => {
	const { t } = useTranslation();
	const {
		// TODO: Uncomment when backend is ready
		setValue,
		watch,
	} = useFormContext();
	const sellingTime: string = watch('sellingTime');
	const offSellingTime: string | null = watch('offSellingTime');
	const initialPrice: number = watch('initialPrice');
	const finalPrice: number = watch('finalPrice');
	const color: string = watch('color');

	const [editingInitialPrice, setIsEditingInitialPrice] = useState(false);
	const [editingFinalPrice, setIsEditingFinalPrice] = useState(false);
	// TODO: Uncomment when backend is ready
	// const allowSecondarySales = watch('allowSecondarySales');
	// const onChangeSecondarySales = () => setValue('allowSecondarySales', !allowSecondarySales);

	const { event } = useGetLastEvent();

	const taxesData = useGetTaxes();

	const calculatedBasePrice = useMemo(() => {
		if (!finalPrice || !taxesData) return null;

		return calculateBasePrice({
			finalPrice: Number(finalPrice),
			taxesData,
			event: event ? event : undefined,
		});
	}, [finalPrice, taxesData]);

	const calculatedFinalPrice = useMemo(() => {
		if (!initialPrice || !taxesData) return null;

		return calculateFinalPrice({
			initialPrice: Number(initialPrice),
			taxesData,
			event: event ? event : undefined,
		});
	}, [initialPrice, taxesData, event]);

	// Update initialPrice when finalPrice changes (user edits finalPrice)
	useEffect(() => {
		if (calculatedBasePrice !== null && finalPrice && editingFinalPrice) {
			setValue('initialPrice', calculatedBasePrice);
		}
		setIsEditingFinalPrice(false);
	}, [calculatedBasePrice, finalPrice, setValue]);

	// Update finalPrice when initialPrice changes (user edits initialPrice)
	useEffect(() => {
		if (calculatedFinalPrice !== null && initialPrice && editingInitialPrice) {
			setValue('finalPrice', calculatedFinalPrice);
		}
		setIsEditingInitialPrice(false);
	}, [calculatedFinalPrice, initialPrice, setValue]);

	return (
		<>
			<Form.Input id="name" name="name" label={t('ticketDetails-ticket-type-name')} />
			<FlexContainer gap="8px" flexDirection="column">
				<Styled.Label>Ticket Color (Optional)</Styled.Label>
				<PunchupColorPicker value={color || ''} onChange={(newColor) => setValue('color', newColor)} />
			</FlexContainer>
			<Styled.WrapperInputs>
				<Form.Input
					type="number"
					id="initialPrice"
					name="initialPrice"
					label={t('ticketDetails-ticket-price')}
					onKeyDown={(e) => {
						allowsDecimals(e);
						setIsEditingInitialPrice(true);
					}}
					step=".01"
				/>
				<Form.Input
					type="number"
					id="finalPrice"
					name="finalPrice"
					label={t('ticketDetails-ticket-smart-price')}
					onKeyDown={(e) => {
						allowsDecimals(e);
						setIsEditingFinalPrice(true);
					}}
					step=".01"
				/>
			</Styled.WrapperInputs>
			<Form.Input
				type="number"
				id="amount"
				name="amount"
				label={t('ticketDetails-quantity')}
				onKeyDown={allowsOnlyNumbers}
			/>
			<Form.InputText
				id="description"
				name="description"
				label={t('ticketDetails-ticket-description')}
				required={false}
			/>
			{/* TODO: Uncomment when backend is ready */}
			{/*
			<Styled.ToggleContainer>
				<Text>{t('ticketDetails-secondary-sales')}</Text>
				<ToolTip
					title={t('ticketDetails-secondary-sales-tooltip-title')}
					text={t('ticketDetails-secondary-sales-tooltip')}
				>
					<Icon iconName="help" />
				</ToolTip>
				<Toggle
					isChecked={allowSecondarySales}
					name="allowSecondarySales"
					id="allowSecondarySales"
					handleChange={onChangeSecondarySales}
				/>
			</Styled.ToggleContainer>
			<Form.Input
				type="number"
				inputMode="numeric"
				id="secondaryRoyalty"
				name="secondaryRoyalty"
				label={t('ticketDetails-ticket-price-fee')}
				info={t('ticketDetails-ticket-price-info')}
				max="100"
				onKeyDown={(e) => allowsOnlyNumbers(e)}
			/> */}

			<Form.InputDateAndTime
				id="sellingTime"
				name="sellingTime"
				label={t('createEvent-tickets-onSale')}
				value={new Date(sellingTime)}
			/>
			<Form.InputDateAndTime
				id="offSellingTime"
				name="offSellingTime"
				label={t('createEvent-tickets-offSale')}
				value={offSellingTime ? new Date(offSellingTime) : null}
				required={false}
			/>
			<Form.InputToggle
				htmlFor="ticketVisibility"
				name="ticketVisibility"
				label={t('ticketDetails-ticket-visbility')}
			/>
			<Form.InputToggle htmlFor="isLottery" name="isLottery" label={t('createEvent-lottery-mode')} />
		</>
	);
};

export { LeftForm };
